.mdd-chatbot, .mdd-chatbot * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.mdd-chatbot {
    --mdd-primary: #00b0b0;
    --mdd-secondary: #0070d0;
    --mdd-bg: #ffffff;
    --mdd-surface: #f5f7fb;
    --mdd-text: #1a2233;
    --mdd-text-muted: #6b7280;
    --mdd-bubble-user: linear-gradient(135deg, var(--mdd-primary), var(--mdd-secondary));
    --mdd-bubble-bot: #eef1f6;
    --mdd-border: rgba(15, 23, 42, 0.08);
    --mdd-shadow: 0 20px 45px rgba(15, 23, 42, 0.18);
    /* Distance from the viewport bottom. Overridden inline from the
       `bottomOffset` / `bottomOffsetMobile` init options. */
    --mdd-offset-bottom: 20px;
    --mdd-offset-bottom-mobile: 12px;

    position: fixed;
    right: 20px;
    bottom: var(--mdd-offset-bottom);
    /* Widget-grade stacking value: the host site's sticky headers, promo popups
       and cookie banners must never overlap the open panel. */
    z-index: 9999;
    font-size: 14px;
    line-height: 1.45;
    color: var(--mdd-text);
}

.mdd-chatbot__launcher {
    width: 58px;
    height: 58px;
    border-radius: 50%;
    border: 0;
    cursor: pointer;
    background: var(--mdd-bubble-user);
    color: #fff;
    box-shadow: var(--mdd-shadow);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 150ms ease, box-shadow 150ms ease;
}

.mdd-chatbot__launcher:hover {
    transform: translateY(-2px) scale(1.03);
}

.mdd-chatbot__launcher svg {
    width: 26px;
    height: 26px;
}

.mdd-chatbot__panel {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 380px;
    max-width: calc(100vw - 24px);
    height: 600px;
    max-height: calc(100vh - var(--mdd-offset-bottom) - 20px);
    background: var(--mdd-bg);
    border-radius: 18px;
    box-shadow: var(--mdd-shadow);
    display: none;
    flex-direction: column;
    overflow: hidden;
    transform: translateY(12px);
    opacity: 0;
    transition: transform 180ms ease, opacity 180ms ease;
    border: 1px solid var(--mdd-border);
}

.mdd-chatbot--open .mdd-chatbot__panel {
    display: flex;
    transform: translateY(0);
    opacity: 1;
}

.mdd-chatbot--open .mdd-chatbot__launcher {
    display: none;
}

.mdd-chatbot__header {
    padding: 4px 16px;
    background: var(--mdd-bubble-user);
    color: #fff;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.mdd-chatbot__avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.35);
    flex-shrink: 0;
}

.mdd-chatbot__title {
    font-weight: 600;
    font-size: 15px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mdd-chatbot__icon-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 0;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 120ms ease;
}

.mdd-chatbot__icon-btn:hover {
    background: rgba(255, 255, 255, 0.3);
}

.mdd-chatbot__icon-btn svg {
    width: 16px;
    height: 16px;
}

.mdd-chatbot__messages {
    flex: 1 !important;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 16px 14px;
    background: var(--mdd-surface);
    scroll-behavior: smooth;
    /* `!important` + the permanent spacer child below defeat host resets such
       as `div:empty { display: none }`, which otherwise collapse the empty
       messages area and push the footer up to the header. */
    display: flex !important;
    flex-direction: column-reverse;
    gap: 10px;
}

.mdd-chatbot__spacer {
    flex: 1 1 auto;
    min-height: 0;
}

.mdd-chatbot__intro-avatar {
    width: 128px;
    height: 128px;
    border-radius: 50%;
    object-fit: cover;
    align-self: center;
    border: 3px solid #fff;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.12);
    margin-top: 8px;
}

.mdd-chatbot__intro {
    background: #fff;
    padding: 14px 16px;
    border-radius: 14px;
    border: 1px solid var(--mdd-border);
    align-self: flex-start;
    max-width: 90%;
    color: var(--mdd-text);
}

.mdd-chatbot__intro p:not(:last-child) {
    margin-bottom: 6px;
}

.mdd-chatbot__bubble {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 16px;
    word-break: break-word;
    animation: mdd-fade 150ms ease;
}

.mdd-chatbot__bubble p:not(:last-child) {
    margin-bottom: 6px;
}

.mdd-chatbot__bubble ul,
.mdd-chatbot__bubble ol {
    padding-left: 0;
    margin: 0;
    list-style-position: inside;
}
.mdd-chatbot__bubble ul ul,
.mdd-chatbot__bubble ul ol,
.mdd-chatbot__bubble ol ol,
.mdd-chatbot__bubble ul ol {
    padding-left: 8px;
}

.mdd-chatbot__bubble ul {
    list-style-type: disc;
}

.mdd-chatbot__bubble ol {
    list-style-type: decimal;
}

.mdd-chatbot__bubble li {
    margin: 0;
}

.mdd-chatbot__bubble code {
    background: rgba(15, 23, 42, 0.08);
    padding: 1px 5px;
    border-radius: 4px;
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 0.9em;
}

.mdd-chatbot__bubble img {
    max-width: 150px;
    border-radius: 10px;
    margin: 6px auto;
}

.mdd-chatbot__bubble a,
.mdd-chatbot__intro a {
    color: inherit;
    /* `!important`: host themes commonly ship `a { text-decoration: none !important }`. */
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 2px;
    transition: text-underline-offset 150ms ease, text-decoration-thickness 150ms ease, color 150ms ease;
}

.mdd-chatbot__bubble a:hover,
.mdd-chatbot__intro a:hover {
    text-underline-offset: 4px;
    text-decoration-thickness: 2px !important;
}

.mdd-chatbot__bubble--bot a:hover,
.mdd-chatbot__intro a:hover {
    color: var(--mdd-primary);
}

.mdd-chatbot__bubble--user {
    align-self: flex-end;
    background: #1e293b;
    color: #fff;
    border-bottom-right-radius: 4px;
}

.mdd-chatbot__bubble--bot {
    align-self: flex-start;
    background: var(--mdd-bubble-bot);
    color: var(--mdd-text);
    border-bottom-left-radius: 4px;
}

.mdd-chatbot__bubble--bot hr {
    border: 0;
    border-top: 1px solid var(--mdd-border);
    margin: 10px 0;
}

.mdd-chatbot__bubble--bot table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 13px;
    display: block;
    overflow-x: auto;
}

.mdd-chatbot__bubble--bot th,
.mdd-chatbot__bubble--bot td {
    border: 1px solid var(--mdd-border);
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
}

.mdd-chatbot__bubble--bot th {
    background: rgba(15, 23, 42, 0.04);
    font-weight: 600;
}

.mdd-chatbot__bubble--error {
    align-self: flex-start;
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fca5a5;
    border-bottom-left-radius: 4px;
}

.mdd-chatbot__typing {
    align-self: flex-start;
    background: var(--mdd-bubble-bot);
    padding: 10px 14px;
    border-radius: 16px 16px 16px 4px;
    display: inline-flex;
    gap: 4px;
    align-items: center;
}

.mdd-chatbot__typing span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--mdd-text-muted);
    animation: mdd-bounce 1.2s infinite ease-in-out both;
}

.mdd-chatbot__typing span:nth-child(1) { animation-delay: -0.32s; }
.mdd-chatbot__typing span:nth-child(2) { animation-delay: -0.16s; }
.mdd-chatbot__typing span:nth-child(3) { animation-delay: 0s; }

.mdd-chatbot__tool-hint {
    font-style: italic;
    color: var(--mdd-text-muted);
    font-size: 13px;
    padding: 4px 0;
}

.mdd-chatbot__caret {
    display: inline-block;
    width: 2px;
    height: 1em;
    margin-left: 2px;
    background: currentColor;
    vertical-align: text-bottom;
    animation: mdd-chatbot-caret-blink 1s steps(2, start) infinite;
}

@keyframes mdd-chatbot-caret-blink {
    to { visibility: hidden; }
}

.mdd-chatbot__presets {
    padding: 10px 14px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    background: var(--mdd-surface);
}

.mdd-chatbot__preset {
    border: 1px solid var(--mdd-border);
    background: transparent;
    color: var(--mdd-primary);
    font-weight: 500;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 13px;
    cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
}

.mdd-chatbot__preset:hover {
    background: color-mix(in srgb, var(--mdd-primary) 8%, transparent);
    border-color: var(--mdd-primary);
    transform: translateY(-1px);
}

.mdd-chatbot__footer {
    border-top: 1px solid var(--mdd-border);
    padding: 10px 12px;
    display: flex;
    gap: 8px;
    align-items: flex-end;
    background: #fff;
    flex-shrink: 0;
}

.mdd-chatbot__input {
    flex: 1;
    border: 1px solid var(--mdd-border);
    border-radius: 14px;
    resize: none;
    padding: 9px 12px;
    font-size: 14px;
    max-height: 120px;
    min-height: 38px;
    outline: none;
    overflow-y: hidden;
    background: var(--mdd-surface);
    color: var(--mdd-text);
    transition: border-color 120ms ease, box-shadow 120ms ease;
}

.mdd-chatbot__input:focus {
    border-color: var(--mdd-primary);
    box-shadow: 0 0 0 3px rgba(0, 176, 176, 0.18);
    background: #fff;
}

.mdd-chatbot__send {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--mdd-bubble-user);
    color: #fff;
    border: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: transform 120ms ease;
}

.mdd-chatbot__send:hover:not(:disabled) {
    transform: scale(1.05);
}

.mdd-chatbot__send:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.mdd-chatbot__send svg {
    width: 16px;
    height: 16px;
}

.mdd-chatbot__mic {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: var(--mdd-surface);
    color: var(--mdd-text-muted);
    border: 1px solid var(--mdd-border);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 120ms ease, color 120ms ease, border-color 120ms ease, transform 120ms ease;
}

.mdd-chatbot__mic:hover:not(:disabled) {
    color: var(--mdd-primary);
    border-color: var(--mdd-primary);
}

.mdd-chatbot__mic--listening {
    background: #fee2e2;
    color: #dc2626;
    border-color: #fca5a5;
    animation: mdd-chatbot-mic-pulse 1.2s ease-in-out infinite;
}

.mdd-chatbot__mic--listening:hover {
    color: #dc2626;
    border-color: #fca5a5;
}

.mdd-chatbot__mic svg {
    width: 16px;
    height: 16px;
}

@keyframes mdd-chatbot-mic-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.35); }
    50% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
}

@keyframes mdd-fade {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes mdd-bounce {
    0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
    40% { transform: translateY(-4px); opacity: 1; }
}

@media (max-width: 480px) {
    .mdd-chatbot {
        right: 12px;
        bottom: var(--mdd-offset-bottom-mobile);
    }

    /* Fullscreen panel: fixed to the viewport instead of absolute to the
       launcher anchor, so the host page is never visible around it. */
    .mdd-chatbot__panel {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        max-width: none;
        height: 100dvh;
        max-height: none;
        border: 0;
        border-radius: 0;
    }

    .mdd-chatbot__header {
        padding-top: calc(4px + env(safe-area-inset-top, 0px));
    }

    .mdd-chatbot__footer {
        padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
    }
}
